<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import UploadField from "@/components/UploadField.vue";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import { uploadImage } from "@/lib/uploads";
import { toDateInput } from "@/lib/date";
import { fetchPromotion, addPromotion, savePromotion, deletePromotion } from "./api";

const route = useRoute(), router = useRouter();
const id = computed(() => route.params.id || null);
const isEdit = computed(() => !!id.value);
const loading = ref(false), notFound = ref(false), saving = ref(false), success = ref(false), error = ref(""), validationError = ref(""), confirmingDelete = ref(false), deleting = ref(false), enabled = ref(false);

const promoTypeItems = [{ title: "Retail", value: "Retail" }, { title: "Pawn", value: "Pawn" }];
const form = ref({ createdBy: "", name: "", code: "", limit: 0, promoType: "Retail", btnTitle: "", percentDiscount: 0, startDate: "", endDate: "", imgUrl: "" });
const imageUploader = (file, p) => uploadImage("promotions", file, p);

onMounted(async () => {
  if (!isEdit.value) return;
  loading.value = true;
  try {
    const p = await fetchPromotion(id.value);
    if (!p) { notFound.value = true; return; }
    form.value.name = p.name ?? ""; form.value.code = p.code ?? ""; form.value.percentDiscount = p.percentDiscount ?? 0; form.value.createdBy = p.createdBy ?? ""; form.value.startDate = toDateInput(p.dateTimeStart); form.value.endDate = toDateInput(p.dateTimeEnd); enabled.value = p.isEnabled;
  } catch { error.value = "Couldn't load the promotion."; }
  finally { loading.value = false; }
});

async function save() {
  success.value = false; error.value = ""; validationError.value = "";
  const f = form.value;
  if (!f.createdBy.trim()) { validationError.value = "Created By is required."; return; }
  if (!f.name.trim()) { validationError.value = "Promotion code is required."; return; }
  if (!f.code.trim()) { validationError.value = "Promotion description is required."; return; }
  saving.value = true;
  try {
    if (isEdit.value) {
      const start = new Date(`${f.startDate}T00:00:00`);
      await savePromotion({ id: id.value, name: f.name, code: f.code, percentDiscount: f.percentDiscount, startDateTime: start, endDateTime: new Date(`${f.endDate}T23:59:59`), createdBy: f.createdBy, updatedDateTime: new Date(), isEnabled: Date.now() < start.getTime() ? false : enabled.value });
    } else {
      await addPromotion({ promoType: f.promoType, imgUrl: f.imgUrl, name: f.name, code: f.code, limit: f.limit, btnTitle: f.btnTitle, startDateTime: new Date(f.startDate), endDateTime: new Date(f.endDate), createdBy: f.createdBy, updatedDateTime: new Date(), isEnabled: false });
    }
    success.value = true; setTimeout(() => router.push({ name: "promotions" }), 800);
  } catch { error.value = "Error occurred. Please try again."; }
  finally { saving.value = false; }
}

async function confirmDelete() {
  deleting.value = true;
  try { await deletePromotion(id.value); router.push({ name: "promotions" }); }
  catch { error.value = "Error occurred."; confirmingDelete.value = false; }
  finally { deleting.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'promotions' })">Back</VBtn>
    <h1 class="text-h5 mb-4">{{ isEdit ? "Edit Promotion" : "Create Promotion" }}</h1>
    <p v-if="loading">Loading…</p>
    <VAlert v-else-if="notFound" type="error">Promotion not found.</VAlert>
    <VCard v-else class="pa-4" max-width="640">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Saved. Redirecting…</VAlert>
      <form @submit.prevent="save">
        <UploadField v-if="!isEdit" v-model="form.imgUrl" kind="image" label="Cover Photo" :uploader="imageUploader" class="mb-4" />
        <VRow>
          <VCol cols="12"><VTextField v-model="form.createdBy" label="Created By" /></VCol>
          <VCol cols="12"><VTextField v-model="form.name" :label="isEdit ? 'Name' : 'Promotion Code'" /></VCol>
          <VCol cols="12"><VTextField v-model="form.code" :label="isEdit ? 'Code' : 'Promotion Description'" /></VCol>
          <template v-if="!isEdit">
            <VCol cols="12" md="6"><VTextField v-model.number="form.limit" label="Usage Limit" type="number" /></VCol>
            <VCol cols="12" md="6"><VSelect v-model="form.promoType" label="Promo Type" :items="promoTypeItems" item-title="title" item-value="value" /></VCol>
            <VCol cols="12"><VTextField v-model="form.btnTitle" label="Button Title" /></VCol>
          </template>
          <template v-else>
            <VCol cols="12"><VTextField v-model.number="form.percentDiscount" label="Discount (%)" type="number" min="1" max="100" /></VCol>
          </template>
          <VCol cols="12" md="6"><VTextField v-model="form.startDate" :label="isEdit ? 'Start Date' : 'Start Date'" :type="isEdit ? 'date' : 'datetime-local'" /></VCol>
          <VCol cols="12" md="6"><VTextField v-model="form.endDate" :label="isEdit ? 'End Date' : 'End Date'" :type="isEdit ? 'date' : 'datetime-local'" /></VCol>
        </VRow>
        <VAlert v-if="validationError" type="warning" class="mb-4 mt-2">{{ validationError }}</VAlert>
        <div class="d-flex gap-2 mt-4 flex-wrap">
          <VBtn type="submit" :loading="saving">{{ isEdit ? "Update" : "Create" }}</VBtn>
          <VBtn variant="tonal" @click="router.push({ name: 'promotions' })">Cancel</VBtn>
          <VSpacer />
          <VBtn v-if="isEdit" color="error" variant="tonal" @click="confirmingDelete = true">Delete</VBtn>
        </div>
      </form>
    </VCard>
    <ConfirmDialog :model-value="confirmingDelete" title="Delete Promotion?" message="This will permanently delete this promotion." confirm-label="Delete" :loading="deleting" @update:model-value="confirmingDelete = false" @confirm="confirmDelete" />
  </section>
</template>
